import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Heading, Loading } from "@foris/avocado-suite";
import { emitEvent, Events, useListenEvent } from "@common/hooks/useListenEvent";
import GroupRestrictions from "@dimensions/group/pages/group-restrictions";
import styles from "./groupRestrictionsDrawer.module.scss";
import { useLayoutStore } from "@context/useLayoutStore";

interface GroupRestrictionsDrawerProps {
  scenario: string;
  origin: string;
  id: string;
  canEdit?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const GroupRestrictionsDrawer = ({
  scenario,
  origin,
  id,
  isOpen,
  onClose,
}: GroupRestrictionsDrawerProps) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useListenEvent({
    eventKey: "on:update",
    handler: (detail: Events["on:update"]) => {
      if (detail?.target === "group-restrictions-changes") {
        setHasChanges(detail?.data?.hasChanges);
      }

      if (detail?.target === "group-restrictions-saving") {
        setIsSaving(detail?.data?.isSaving);
      }
    },
  });

  useEffect(() => {
    setHasChanges(false);
  }, [isOpen]);

  return (
    <>
      {isSaving && <Loading />}
      <Drawer
        contentClassName={styles.groupRestrictionsDrawer_content}
        className={styles.groupRestrictionsDrawer}
        isOpen={isOpen}
        heightGap={visibleHeaderPixels}
        onClose={onClose}
        closeOnOverlayClick
        title={<Heading type="h3">{t("group-restrictions.manage-restrictions.title")}</Heading>}
        footer={
          <footer className={styles.groupRestrictionsDrawer_actionsButton}>
            <Button variant="ghost" onClick={onClose}>
              {t("generals.cancel")}
            </Button>

            <Button
              variant="primary"
              disabled={!hasChanges || isSaving}
              onClick={() => emitEvent("on:submit", { target: "group-restrictions-save-changes" })}
            >
              {t("generals.save-changes")}
            </Button>
          </footer>
        }
      >
        {isOpen && (
          <GroupRestrictions
            scenario={scenario}
            origin={origin}
            id={id}
            onSuccess={() => {
              onClose?.();
              emitEvent("on:refresh", { target: "group-calendar" });
            }}
          />
        )}
      </Drawer>
    </>
  );
};

export default GroupRestrictionsDrawer;
